import type { MacWifiBridge, MacWifiResult } from '../../api/wifi-types.js';
import { h, type View } from './dom.js';

const MAC_FALLBACK = 'No se pudo leer la información de macOS. Puedes escribir la contraseña.';

/** «Usar la Wi-Fi de este Mac» (Mac app only): lists the Mac's known networks and reads one password from the
 * Keychain after the person's click and macOS's own prompt. The password goes straight to onSend and is not kept. */
export function createMacNetworkChooser(options: {
  mac: MacWifiBridge;
  onSend(ssid: string, psk: string): void;
  /** Reading failed or was refused: continue by typing the password for this network. */
  onManual(ssid: string): void;
  onBack(): void;
}): View {
  let alive = true, reading = false, token = 0;
  const status = h('p', { class: 'card-sub', role: 'status', 'aria-live': 'polite' }, ['Buscando las redes que conoce este Mac…']);
  const list = h('ul', { class: 'wireless-choices', 'aria-label': 'Redes Wi-Fi de este Mac' });
  const back = h('button', { class: 'btn ghost', type: 'button' }, ['Escribir una red']);
  const root = h('div', { class: 'form-stack mac-network-chooser' }, [
    h('p', { class: 'card-sub' }, ['Elige la red de 2,4 GHz que usará ASIS. macOS te pedirá permiso para leer su contraseña; ASIS la envía cifrada al aparato y no la guarda.']),
    status, list, h('div', { class: 'form-row' }, [back]),
  ]);
  async function choose(ssid: string, trigger: HTMLButtonElement): Promise<void> {
    if (!alive || reading) return;
    const my = ++token; reading = true;
    for (const button of list.querySelectorAll('button')) button.disabled = true;
    // Called synchronously from the click: no await may come before the bridge call.
    const pending = options.mac.readPassword(ssid);
    status.textContent = 'Esperando la autorización de macOS. Puede tardar hasta un minuto.';
    let result: MacWifiResult<{ psk: string }>;
    try { result = await pending; } catch { result = { ok: false, code: 'failed', message: MAC_FALLBACK }; }
    reading = false;
    if (!alive || my !== token) { if (result.ok) result.psk = ''; return; }
    for (const button of list.querySelectorAll('button')) button.disabled = false;
    if (!result.ok) {
      if (result.code === 'busy') { status.textContent = result.message || MAC_FALLBACK; trigger.focus(); return; }
      options.onManual(ssid);
      return;
    }
    let psk = result.psk; result.psk = '';
    options.onSend(ssid, psk);
    psk = '';
  }
  async function load(): Promise<void> {
    let result: MacWifiResult<{ ssids: string[] }>;
    try { result = await options.mac.listSaved(); } catch { result = { ok: false, code: 'failed', message: MAC_FALLBACK }; }
    if (!alive) return;
    if (!result.ok) { status.textContent = result.message || MAC_FALLBACK; return; }
    if (!result.ssids.length) { status.textContent = 'Este Mac no tiene redes Wi-Fi guardadas. Escribe la red a mano.'; return; }
    status.textContent = '';
    list.replaceChildren(...result.ssids.map(ssid => {
      const button = h('button', { class: 'btn ghost', type: 'button' });
      button.textContent = ssid;
      button.addEventListener('click', () => { void choose(ssid, button); });
      return h('li', {}, [button]);
    }));
  }
  back.addEventListener('click', () => { if (!reading) options.onBack(); });
  void load();
  return {
    el: root,
    destroy() {
      alive = false; token++;
      if (reading) { try { void Promise.resolve(options.mac.cancel?.()).catch(() => undefined); } catch { /* best effort */ } }
    },
  };
}
